import React, { useRef, useEffect, useState, useMemo } from 'react';
import styles from './Fireworks.css';

interface FireworksProps {
    visible: boolean;
    onAnimationEnd?: () => void;
    duration?: number;
}

interface Firework {
    id: number;
    left: number;
    delay: number;
    size: number;
    particleSize: number;
    particleCount: number;
    color: string;
}

interface Sparkle {
    id: number;
    left: number;
    top: number;
    delay: number;
    size: number;
    color: string;
}

const Fireworks: React.FC<FireworksProps> = ({ visible, onAnimationEnd, duration = 5000 }) => {
    const [isAnimating, setIsAnimating] = useState(false);
    const animationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const brightFireworkColors = [
        '#FF3B5A', '#FF8700', '#25DF7B', '#B76EFF', '#4485FF',
        '#00E0D0', '#00BBFF', '#FFD700', '#FF1493', '#39FF14', '#00FFFF'
    ];

    const fireworks = useMemo<Firework[]>(() => 
        Array.from({ length: 8 }, (_, i) => ({
            id: i,
            left: Math.random() * 80 + 10,
            delay: Math.random() * 1500,
            size: Math.random() * 20 + 20,
            particleSize: Math.random() * 2 + 2,
            particleCount: Math.floor(Math.random() * 6) + 6,
            color: brightFireworkColors[Math.floor(Math.random() * brightFireworkColors.length)],
        }))
    , [visible]);

    const sparkles = useMemo<Sparkle[]>(() =>
        Array.from({ length: 3 }, (_, i) => ({
            id: i,
            left: Math.random() * 80 + 10,
            top: Math.random() * 60 + 10,
            delay: Math.random() * 2000,
            size: Math.random() * 4 + 3,
            color: ['#FFFFFF', '#FFFF00', '#FFD700'][Math.floor(Math.random() * 3)],
        }))
    , [visible]);

    useEffect(() => {
        if (visible) {
            setIsAnimating(true);
            
            if (animationTimerRef.current) {
                clearTimeout(animationTimerRef.current);
            }

            animationTimerRef.current = setTimeout(() => {
                setIsAnimating(false);
                onAnimationEnd?.();
            }, duration);
        } else {
            setIsAnimating(false);
        }

        return () => {
            if (animationTimerRef.current) {
                clearTimeout(animationTimerRef.current);
            }
        };
    }, [visible, duration, onAnimationEnd]);

    if (!isAnimating) {
        return null;
    }

    return (
        <div className={styles.container}>
            {fireworks.map(fw => (
                <div
                    key={fw.id}
                    className={styles.firework}
                    style={{
                        left: `${fw.left}%`,
                        animationDelay: `${fw.delay}ms`,
                    }}
                >
                    {Array.from({ length: fw.particleCount }, (_, i) => (
                        <div
                            key={i}
                            className={styles.particle}
                            style={{
                                backgroundColor: fw.color,
                                width: fw.particleSize,
                                height: fw.particleSize,
                                transform: `rotate(${(360 / fw.particleCount) * i}deg)`,
                                animationDelay: `${fw.delay}ms`,
                            }}
                        />
                    ))}
                </div>
            ))}
            {sparkles.map(sp => (
                <div
                    key={`sparkle-${sp.id}`}
                    className={styles.sparkle}
                    style={{
                        left: `${sp.left}%`,
                        top: `${sp.top}%`,
                        width: sp.size,
                        height: sp.size,
                        backgroundColor: sp.color,
                        animationDelay: `${sp.delay}ms`,
                    }}
                />
            ))}
        </div>
    );
};

export default Fireworks;
